import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';

@Component({
  selector: 'app-street-view',
  templateUrl: './street-view.component.html',
  styleUrls: ['./street-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StreetViewComponent implements OnInit, OnChanges, OnDestroy {
  @Input() defaultPlace;
  @Input() isMyHomeAccess;
  @Input() containerHeight: string;

  @Output() streetViewEvent = new EventEmitter<any>();
  @Output() streetViewAvailable = new EventEmitter<boolean>();

  @ViewChild('streetView', { static: true }) streetView: ElementRef;

  streetViewInitVal: ElementRef;
  isError = false;

  private panorama: any;
  private changeRequestId = 0;

  constructor(private cdr: ChangeDetectorRef) { }

  ngOnInit() { }

  ngOnChanges(): void {
    this.initializeStreetView();
  }

  private async initializeStreetView(): Promise<void> {
    const requestId = ++this.changeRequestId;
    this.streetViewInitVal = this.streetView;

    if (!this.defaultPlace || !this.streetView?.nativeElement) {
      this.isError = false;
      this.streetViewAvailable.emit(true);
      this.cdr.markForCheck();
      return;
    }

    try {
      const googleMaps = (google as any).maps;

      if (!googleMaps?.importLibrary) {
        this.isError = true;
        this.streetViewAvailable.emit(false);
        this.cdr.markForCheck();
        return;
      }

      const streetViewLibrary: any = await googleMaps.importLibrary(
        'streetView'
      );

      const geometryLibrary: any = await googleMaps.importLibrary(
        'geometry'
      );

      // Ignore an older async request if defaultPlace changes quickly.
      if (requestId !== this.changeRequestId) {
        return;
      }

      const StreetViewService = streetViewLibrary.StreetViewService;
      const StreetViewPanorama = streetViewLibrary.StreetViewPanorama;

      const streetViewService = new StreetViewService();

      this.panorama = new StreetViewPanorama(
        this.streetView.nativeElement,
        {
          position: this.defaultPlace,
          addressControl: false,
          scrollwheel: false,
        }
      );

      streetViewService.getPanorama(
        {
          location: this.defaultPlace,
          radius: 50,
        },
        (data: any, status: any) => {
          if (requestId !== this.changeRequestId) {
            return;
          }

          if (
            status === google.maps.StreetViewStatus.OK &&
            data?.location?.latLng
          ) {
            this.isError = false;
            this.streetViewAvailable.emit(true);

            const headingDirection =
              geometryLibrary.spherical.computeHeading(
                data.location.latLng,
                this.defaultPlace
              );

            this.panorama.setPov({
              heading: headingDirection,
              pitch: 0,
            });
          } else {
            this.isError = true;
            this.streetViewAvailable.emit(false);
          }

          this.cdr.markForCheck();
        }
      );

      this.streetViewEvent.emit(this.panorama);
      this.cdr.markForCheck();
    } catch (error) {
      console.error('Unable to initialize Google Street View:', error);
      this.isError = true;
      this.streetViewAvailable.emit(false);
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    ++this.changeRequestId;

    if (this.panorama) {
      this.panorama.setVisible(false);
      this.panorama = null;
    }

    this.defaultPlace = null;
  }
}
